import mixpanel from 'mixpanel-browser';
import { useEffect, useRef } from 'react';

import { BuildADashTrackingEvents } from '@revelio/core';

import type { ReportParameterConfig } from '../CustomDash/components/PageConfigProvider/page-reducer';
import { useFilterContext } from '../CustomDash/filters/contexts/FilterProvider';
import { getFilterDefinition } from '../CustomDash/filters/engine/filterRegistry';
import {
  FilterType,
  type ListFilter,
  type TreeFilter,
} from '../CustomDash/filters/types/filters';
import type { GlobalAssistantReportParamSuggestion } from './global-ai-assistant-api';
import {
  globalAssistantReportLaunchPendingStore,
  useGlobalAssistantReportLaunchPendingStore,
} from './global-ai-assistant-report-launch-pending';
import {
  countResolvedSuggestions,
  groupSuggestionsByReportParameter,
} from './global-ai-assistant-report-param-mapping';
import { GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS } from './global-ai-assistant-report-parameter-catalog';
import {
  toListSelectionFromResolved,
  toPromotedReportTreeSelectionFromResolved,
} from './global-ai-assistant-report-resolved-selection';

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';

const buildUpdatedFilter = (
  parameter: ReportParameterConfig,
  suggestions: GlobalAssistantReportParamSuggestion[],
  getFilterData: ReturnType<typeof useFilterContext>['getFilterData']
): TreeFilter | ListFilter | null => {
  const definition = getFilterDefinition(parameter.filterId);
  if (!definition) {
    return null;
  }

  if (definition.type === FilterType.TREE) {
    const selections = suggestions
      .map((suggestion) =>
        suggestion.resolvedSelection
          ? toPromotedReportTreeSelectionFromResolved({
              resolved: suggestion.resolvedSelection,
              filterId: parameter.filterId,
              getFilterData,
            })
          : null
      )
      .filter(
        (selection): selection is NonNullable<typeof selection> =>
          selection !== null
      );
    if (selections.length === 0) {
      return null;
    }
    if (parameter.isPrimary) {
      return {
        ...definition,
        isPrimary: true,
        selections,
        primarySelections: selections.map((selection) => ({
          ...selection,
          isActive: true,
        })),
        isUserSubmitted: true,
      };
    }
    return {
      ...definition,
      selections,
      isUserSubmitted: true,
    };
  }

  if (definition.type === FilterType.LIST) {
    const selections = suggestions
      .map((suggestion) =>
        suggestion.resolvedSelection
          ? toListSelectionFromResolved(suggestion.resolvedSelection)
          : null
      )
      .filter(
        (selection): selection is NonNullable<typeof selection> =>
          selection !== null
      );
    if (selections.length === 0) {
      return null;
    }
    return {
      ...definition,
      selections,
      isUserSubmitted: true,
    };
  }

  return null;
};

export const useGlobalAssistantPromotedReportPrefill = ({
  parameters,
  reportType: reportTypeOverride,
}: {
  parameters: ReportParameterConfig[];
  reportType?: string;
}) => {
  const { dispatch, filterState, getFilterData } = useFilterContext();
  const promotedTemplateId = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.promotedTemplateId
  );
  const paramSuggestions = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.paramSuggestions
  );
  const pendingReportType = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.reportType
  );
  const launchSeq = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.launchSeq
  );
  const launchGenerationRef = useRef(0);
  const fulfilledParametersRef = useRef<{
    launchKey: string | null;
    launchSeq: number;
    parameterIds: Set<string>;
    appliedSuggestionCount: number;
  }>({
    launchKey: null,
    launchSeq: 0,
    parameterIds: new Set(),
    appliedSuggestionCount: 0,
  });

  useEffect(() => {
    const reportType = reportTypeOverride ?? pendingReportType ?? undefined;

    if (!promotedTemplateId || paramSuggestions.length === 0) {
      return;
    }

    if (parameters.length === 0) {
      return;
    }

    const catalogEntry = GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS.find(
      (entry) => entry.reportType === reportType
    );
    const parameterMappings = catalogEntry?.parameters.map((parameter) => ({
      parameterId: parameter.parameterId,
      promotedParameterIds: parameter.promotedParameterIds,
    }));

    const groupedForReadyCheck = groupSuggestionsByReportParameter(
      paramSuggestions,
      parameters,
      parameterMappings
    );
    const requiredSecondaryGroupIds = [...groupedForReadyCheck.keys()]
      .filter((parameter) => !parameter.isPrimary)
      .map((parameter) => parameter.parameterId);
    const groupsReady =
      requiredSecondaryGroupIds.length === 0 ||
      requiredSecondaryGroupIds.every((parameterId) =>
        filterState.groupMap.has(parameterId)
      );
    if (!groupsReady) {
      return;
    }

    const launchKey = `${promotedTemplateId}:${paramSuggestions
      .map((suggestion) => `${suggestion.parameterId}:${suggestion.searchTerm}`)
      .join('|')}`;
    if (
      fulfilledParametersRef.current.launchKey !== launchKey ||
      fulfilledParametersRef.current.launchSeq !== launchSeq
    ) {
      fulfilledParametersRef.current = {
        launchKey,
        launchSeq,
        parameterIds: new Set(),
        appliedSuggestionCount: 0,
      };
    }

    const generation = launchGenerationRef.current + 1;
    launchGenerationRef.current = generation;

    void (async () => {
      try {
        const groupedSuggestions = groupedForReadyCheck;

        for (const [parameter, suggestions] of groupedSuggestions.entries()) {
          if (launchGenerationRef.current !== generation) {
            return;
          }
          if (
            fulfilledParametersRef.current.parameterIds.has(
              parameter.parameterId
            )
          ) {
            continue;
          }

          const updatedFilter = buildUpdatedFilter(
            parameter,
            suggestions,
            getFilterData
          );
          if (launchGenerationRef.current !== generation) {
            return;
          }
          if (!updatedFilter) {
            continue;
          }

          dispatch({
            type: 'SET_FILTER',
            filter: updatedFilter,
            groupKeyOverride: parameter.isPrimary
              ? undefined
              : parameter.parameterId,
          });
          fulfilledParametersRef.current.parameterIds.add(
            parameter.parameterId
          );
          fulfilledParametersRef.current.appliedSuggestionCount +=
            suggestions.filter(
              (suggestion) => suggestion.resolutionStatus === 'resolved'
            ).length;
        }

        if (launchGenerationRef.current !== generation) {
          return;
        }

        const appliedCount =
          fulfilledParametersRef.current.appliedSuggestionCount;
        const metrics = {
          extractedCount: paramSuggestions.length,
          resolvedCount: countResolvedSuggestions(paramSuggestions),
          appliedCount,
          skippedCount: paramSuggestions.length - appliedCount,
        };
        globalAssistantReportLaunchPendingStore
          .getState()
          .actions.clearPromotedLaunch(metrics);
        mixpanel.track(
          BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL_APPLIED,
          {
            global_assistant_report_type: reportType,
            global_assistant_report_prefill_extracted: metrics.extractedCount,
            global_assistant_report_prefill_resolved: metrics.resolvedCount,
            global_assistant_report_prefill_applied: metrics.appliedCount,
            global_assistant_report_prefill_skipped: metrics.skippedCount,
          }
        );
      } catch (error) {
        if (launchGenerationRef.current !== generation || isAbortError(error)) {
          return;
        }
        globalAssistantReportLaunchPendingStore
          .getState()
          .actions.clearPromotedLaunch();
      }
    })();

    return () => {
      if (launchGenerationRef.current === generation) {
        launchGenerationRef.current += 1;
      }
    };
  }, [
    dispatch,
    filterState.groupMap,
    getFilterData,
    launchSeq,
    paramSuggestions,
    parameters,
    pendingReportType,
    promotedTemplateId,
    reportTypeOverride,
  ]);
};
